// src/About.js
import React, { useEffect, useState } from 'react';
import './LoriTemplate.css';
// Import the image properly
import WelcomeImage from '../components/welcomeImage/WelcomeImage';
import EventCalendar from '../components/calendar/EventCalendar';
import ContentSection from '../components/contentSection/ContentSection';
import LocationSection from '../components/locationSection/LocationSection';
import MediaAsset from '../components/mediaAsset/MediaAsset';
import Footer from '../components/footer/Footer';
import ThankYouSection from '../components/thankYouSection/ThankYouSection';
import MessageSection from '../components/messageSection/MessageSection';
import DressCodeSection from '../components/dressCodeSection/DressCodeSection';
import PhoneContainer from '../components/phoneContainer/PhoneContainer';
import CountdownContainer from '../components/countdownContainer/CountdownContainer';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSubdomain } from '../utils/routingUtils';
import AttendanceQuestions from '../components/attendanceQuestions/AttendanceQuestions';
import SpotifySection from '../components/spotifySection/SpotifySection';
import ComponentInspector from '../components/componentInspector/ComponentInspector';
import DeploymentsBubble from '../components/DeploymentsBubble/DeploymentsBubble';
import ReactCountryFlag from 'react-country-flag';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
      <br/>
       <p>Loading...</p>
  </div>
);

function LoriTemplate({ editorViewEnabled = false, subdomainId = null }) {
  const params = useParams();
    const [themeLoaded, setThemeLoaded] = useState(false);
     const { id: routeId, token } = params;
     const id = subdomainId || routeId;
     const navigate = useNavigate();
     const location = useLocation();
     const [template, setTemplate] = useState(null);
     const [editedTemplate, setEditedTemplate] = useState(null);
     const [currentLang, setCurrentLang] = useState(null);
     const [multiLanguage, setMultiLanguage] = useState(false);
     const [selectedLanguages, setSelectedLanguages] = useState([]);
     const [deployments, setDeployments] = useState([]);
     const [loadedId, setLoadedId] = useState(null);
     // eslint-disable-next-line no-unused-vars
     const [originalTemplate, setOriginalTemplate] = useState(null);
     const [homeHover, setHomeHover] = useState(false);
     const [showList, setShowList] = useState(false);
     const [showLangModal, setShowLangModal] = useState(false);
     const [langModalOptions, setLangModalOptions] = useState([]);

     const loadThemeColors = async (colors) => {
       try {
          const root = document.documentElement;

          root.style.setProperty('--primary-color', colors.primary);
          root.style.setProperty('--secondary-color', colors.secondary);
          root.style.setProperty('--background-color', colors.background);
          root.style.setProperty('--highlight-color', colors.highlight);
          root.style.setProperty('--border-color', colors.border);
          root.style.setProperty('--icon-color', colors.icon);

          setThemeLoaded(true);
       } catch (error) {
         console.error('Error loading theme:', error);
         setThemeLoaded(true); // Set to true even on error to fall back to CSS defaults
        }
     };

     // Function to clear URL language parameter
     const clearUrlLanguageParam = () => {
       if (!editorViewEnabled) {  // Only clear URL in non-editor mode
         const searchParams = new URLSearchParams(location.search);
         searchParams.delete('lang');
         const newSearch = searchParams.toString();
         const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
         navigate(newPath, { replace: true });
       }
     };

     // Function to redirect to appropriate home based on routing context
     const redirectToHome = () => {
       const subdomain = getSubdomain();
       if (subdomain) {
         // For subdomain routing, redirect to main domain home
         window.location.href = `${window.location.protocol}//${window.location.hostname.replace(subdomain + '.', '')}${window.location.port ? ':' + window.location.port : ''}`;
       } else {
         // For normal routing, redirect to /invites (project list)
         navigate('/');
       }
     };

     // Handles the fetched template data after it is received
     function handleFetchedTemplateData(data) {
       loadThemeColors(data.colors);
       setTemplate(data);
       setEditedTemplate(data);
       // Language logic
       if (data.functions && typeof data.functions === 'object') {
         const keys = Object.keys(data.functions);
         const searchParams = new URLSearchParams(location.search);
         const urlLang = searchParams.get('lang');
         
         if (keys.length === 1 && keys[0] === '__') {
           // Single language template
           setMultiLanguage(false);
           setSelectedLanguages([]);
           setCurrentLang('__');
         } else {
           // Multi-language template
           setMultiLanguage(true);
           setSelectedLanguages(keys);
           
           // Check if URL has a valid language code that exists in the template
           if (urlLang && keys.includes(urlLang)) {
             setCurrentLang(urlLang);
           } else {
             // Use first available language as default
             setCurrentLang(keys[0]);
             // Clear invalid language parameter from URL
             if (urlLang) {
               clearUrlLanguageParam();
             }
           }
         }
       }
     }

     const fetchTemplate = async (url) => {
       try {
         const res = await fetch(url);
         if (!res.ok) {
           throw new Error('Network response was not ok');
         }
         const data = await res.json();
         handleFetchedTemplateData(JSON.parse(data));
       } catch (error) {
         console.error("Error fetching template:", error);
         redirectToHome();
       }
     };
     
     async function fetchAndSetActiveDeployment(endpoint) {
      try {
        const token = sessionStorage.getItem('token');
        const deploymentsRes = await fetch(`${endpoint}/stored`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (deploymentsRes.status === 401) {
          navigate('/login');
          return;
        }
        if (!deploymentsRes.ok) throw new Error('Failed to fetch deployments');
        const deploymentsData = await deploymentsRes.json();
        setDeployments(sortDeploymentsByDate(deploymentsData));
        // Find the active deployment
        const activeDeployment = deploymentsData.find(d => d.active) || deploymentsData[0];
        if (activeDeployment) {
          setLoadedId(activeDeployment.id);
          handleFetchedTemplateData(JSON.parse(activeDeployment.template));
        } else {
          // No deployments found, redirect to home
          redirectToHome();
        }
      } catch (err) {
        console.error(err);
        redirectToHome();
      }
    }

     useEffect(() => {
      const endpoint = `${API_URL}/api/event-invites/template/${id}`;

      if (!editorViewEnabled) {
        fetchTemplate(endpoint);
      } else {
        // Editor mode: fetch all deployments, then fetch the active one
        fetchAndSetActiveDeployment(endpoint);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [id, token, editorViewEnabled]);

     // Show language selection modal if not in editor mode and multiple languages
     useEffect(() => {
       if (!editorViewEnabled && template && template.functions && typeof template.functions === 'object') {
         const keys = Object.keys(template.functions);
         const searchParams = new URLSearchParams(location.search);
         const urlLang = searchParams.get('lang');
         
         if (keys.length > 1) {
           // Only show modal if there's no valid language parameter in URL
           if (!urlLang || !keys.includes(urlLang)) {
             setLangModalOptions(keys);
             setShowLangModal(true);
           } else {
             setShowLangModal(false);
           }
         } else {
           setShowLangModal(false);
         }
       }
     }, [editorViewEnabled, template, location.search]);

     const handleTemplateUpdate = (newTemplate) => {
       setEditedTemplate(newTemplate);
     };

     const handleLanguagesChange = (langs) => {
       setSelectedLanguages(langs);
       if (langs.length === 0) {
         const lastLang = selectedLanguages.length > 0 ? selectedLanguages[selectedLanguages.length - 1] : null;
         const lastContext = lastLang && editedTemplate && editedTemplate.functions && editedTemplate.functions[lastLang]
           ? editedTemplate.functions[lastLang]
           : [];
         setEditedTemplate({
           ...editedTemplate,
           functions: { '__': lastContext }
         });
         setCurrentLang('__');
         setMultiLanguage(false);
       }
     };

     const handleMultiLanguageToggle = () => {
       if (multiLanguage) {
         const lastContext = currentLang && editedTemplate && editedTemplate.functions && editedTemplate.functions[currentLang]
           ? editedTemplate.functions[currentLang]
           : [];
         setEditedTemplate({
           ...editedTemplate,
           functions: { '__': lastContext }
         });
         setCurrentLang('__');
         setSelectedLanguages([]);
         setMultiLanguage(false);
       } else {
         setMultiLanguage(true);
       }
     };

const functionComponents = {
  WelcomeImage,
  EventCalendar,
  MediaAsset,
  LocationSection,
  MessageSection,
  ContentSection,
  DressCodeSection,
  ThankYouSection,
  CountdownContainer,
  AttendanceQuestions,
  SpotifySection
};
    
    if (!themeLoaded || !template) {
      return <LoadingSpinner />;
    }    

    // Use editedTemplate for rendering if available, otherwise fall back to original template
    const templateToRender = editedTemplate || template;

    const availableLanguages = templateToRender.functions ? Object.keys(templateToRender.functions) : [];
    const functionsToRender = (
      currentLang && templateToRender.functions && templateToRender.functions[currentLang]
    )
      ? templateToRender.functions[currentLang]
      : (
          templateToRender.functions && templateToRender.functions['__']
            ? templateToRender.functions['__']
            : []
        );

    const content = (
      <>
        {functionsToRender.map((func, index) => {
          const Component = functionComponents[func.name];
          // Pass playlistName (invite ID) to SpotifySection only
          if (func.name === 'SpotifySection') {
            return Component ? <Component key={index} {...func.props} editorViewEnabled={editorViewEnabled} playlistName={id} /> : null;
          }
          return Component ? <Component key={index} {...func.props} editorViewEnabled={editorViewEnabled} templateKey={id} /> : null;
        })}
        <Footer />
      </>
    );

    const inspectorContent = (
      <ComponentInspector 
        template={templateToRender} 
        isVisible={true}
        onUpdate={handleTemplateUpdate}
        currentLang={currentLang}
        setCurrentLang={setCurrentLang}
        availableLanguages={availableLanguages}
        multiLanguage={multiLanguage}
        selectedLanguages={selectedLanguages}
        onLanguagesChange={handleLanguagesChange}
        onMultiLanguageToggle={handleMultiLanguageToggle}
      />
    );

    // Helper to extract only the core template data for comparison
    function getCoreData(obj) {
      if (!obj) return obj;
      const { functions, colors, name, key, _id, createdAt, updatedAt } = obj;
      return { functions, colors, name, key, _id, createdAt, updatedAt };
    }
    
    const hasUnsavedChanges = template && editedTemplate && JSON.stringify(getCoreData(template)) !== JSON.stringify(getCoreData(editedTemplate));

    const handleSaveDeployment = async (name) => {
      const newDeployment = {
        name: name || 'Untitled',
        templatename: id,
        template: JSON.stringify(editedTemplate)
      };

      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/event-invites/template/stored`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(newDeployment)
      });
      if (res.status === 401) {
        navigate('/login');
        return;
      }
      if (!res.ok) throw new Error('Failed to save deployment');
      const saved = await res.json();
      
      setDeployments(sortDeploymentsByDate([saved, ...deployments]));
      setLoadedId(saved.id);
      handleLoadDeployment(saved);
    };

    const handleLoadDeployment = (deployment) => {
      let templateObj = deployment?.template || deployment;
      if (typeof templateObj === 'string') {
        try {
          templateObj = JSON.parse(templateObj);
        } catch (e) {
          alert('Template is not valid JSON.');
          return;
        }
      }
      const clone = typeof structuredClone === 'function' ? structuredClone(templateObj) : JSON.parse(JSON.stringify(templateObj));
      setTemplate(clone);
      setEditedTemplate(typeof structuredClone === 'function' ? structuredClone(templateObj) : JSON.parse(JSON.stringify(templateObj)));
      setOriginalTemplate(clone);
      // Ensure currentLang is set correctly after loading
      const keys = Object.keys(clone.functions || {});
      if (keys.length === 1 && keys[0] === '__') {
        setCurrentLang('__');
        setSelectedLanguages([]); // Clear previous languages
        setMultiLanguage(false);
      } else if (keys.length > 0) {
        setCurrentLang(keys[0]);
        setSelectedLanguages(keys);
        setMultiLanguage(true);
      }
    };

    // Helper to sort deployments by created date descending
    function sortDeploymentsByDate(deployments) {
      return deployments.slice().sort((a, b) => (b.created || '').localeCompare(a.created || ''));
    }

    // Curated language list for flags
    const curatedLanguages = [
      { code: 'en', name: 'English', countryCode: 'GB' },
      { code: 'de', name: 'German', countryCode: 'DE' },
      { code: 'fr', name: 'French', countryCode: 'FR' },
      { code: 'es', name: 'Spanish', countryCode: 'ES' },
      { code: 'it', name: 'Italian', countryCode: 'IT' },
      { code: 'nl', name: 'Dutch', countryCode: 'NL' },
      { code: 'pl', name: 'Polish', countryCode: 'PL' },
      { code: 'pt', name: 'Portuguese', countryCode: 'PT' },
      { code: 'ru', name: 'Russian', countryCode: 'RU' },
      { code: 'uk', name: 'Ukrainian', countryCode: 'UA' },
      { code: 'el', name: 'Greek', countryCode: 'GR' },
      { code: 'sv', name: 'Swedish', countryCode: 'SE' },
      { code: 'cs', name: 'Czech', countryCode: 'CZ' },
      { code: 'hu', name: 'Hungarian', countryCode: 'HU' },
      { code: 'ro', name: 'Romanian', countryCode: 'RO' },
      { code: 'da', name: 'Danish', countryCode: 'DK' },
      { code: 'fi', name: 'Finnish', countryCode: 'FI' },
      { code: 'no', name: 'Norwegian', countryCode: 'NO' },
      { code: 'ar', name: 'Arabic', countryCode: 'SA' },
      { code: 'zh', name: 'Chinese (Simplified)', countryCode: 'CN' },
      { code: 'ja', name: 'Japanese', countryCode: 'JP' },
      { code: 'tr', name: 'Turkish', countryCode: 'TR' },
      { code: 'he', name: 'Hebrew', countryCode: 'IL' },
    ];

    // Modal for language selection
    const renderLanguageModal = () => (
      <div className="lang-modal-overlay">
        <div className="lang-modal-content">
          <div className="lang-modal-flags">
            {langModalOptions.map(code => {
              if (code === '__') return null;
              const lang = curatedLanguages.find(l => l.code === code);
              return (
                <button
                  key={code}
                  className="lang-modal-flag-btn"
                  onClick={() => {
                    setCurrentLang(code);
                    setShowLangModal(false);
                  }}
                >
                  <div className="lang-modal-flag-container">
                    <ReactCountryFlag
                      countryCode={lang ? lang.countryCode : code.toUpperCase()}
                      svg
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                      }}
                      title={code}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );

    return (
      <>
        {showLangModal && renderLanguageModal()}
        {editorViewEnabled && (
          <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 2100, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
            {!showList && (
              <button
                onClick={() => navigate('/invites')}
                title="Go Home"
                onMouseEnter={() => setHomeHover(true)}
                onMouseLeave={() => setHomeHover(false)}
                onFocus={() => setHomeHover(true)}
                onBlur={() => setHomeHover(false)}
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: '#fff',
                  border: 'none',
                  boxShadow: homeHover ? '0 4px 16px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 6,
                  transition: 'box-shadow 0.2s',
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    transition: 'transform 0.2s',
                    transform: homeHover ? 'scale(1.07)' : 'none',
                  }}
                >
                  <img
                    src={require('../assets/favicon.png')}
                    alt="Invite Avenue Logo"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </span>
              </button>
            )}
            <DeploymentsBubble
              hasUnsavedChanges={hasUnsavedChanges}
              onSaveDeployment={handleSaveDeployment}
              deployments={deployments}
              setDeployments={setDeployments}
              loadedId={loadedId}
              setLoadedId={setLoadedId}
              onLoadDeployment={handleLoadDeployment}
              showList={showList}
              setShowList={setShowList}
            />
          </div>
        )}
        <div className="template-container">
          {editorViewEnabled && (
            <div className="floating-inspector">
              <PhoneContainer>{inspectorContent}</PhoneContainer>
            </div>
          )}
          <div className="phone-container-wrapper">
            <PhoneContainer>{content}</PhoneContainer>
          </div>
        </div>
      </>
    );
  }

  export default LoriTemplate;